import { useState, useCallback } from 'react';
import { handleApiError, retryOperation } from '../utils/errorHandling';

interface UseErrorHandling {
  error: string | null;
  setError: (error: string | null) => void;
  handleError: (error: unknown, context: string) => Promise<void>;
  clearError: () => void;
  withErrorHandling: <T>(operation: () => Promise<T>, context: string) => Promise<T | undefined>;
}

export function useErrorHandling(): UseErrorHandling {
  const [error, setError] = useState<string | null>(null);

  const handleError = useCallback(async (error: unknown, context: string) => {
    const userMessage = await handleApiError(error, context);
    setError(userMessage);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const withErrorHandling = useCallback(async <T>(
    operation: () => Promise<T>,
    context: string
  ): Promise<T | undefined> => {
    try {
      clearError();
      return await retryOperation(operation);
    } catch (error) {
      await handleError(error, context);
      return undefined;
    }
  }, [clearError, handleError]);

  return {
    error,
    setError,
    handleError,
    clearError,
    withErrorHandling
  };
}